import * as Colors from '@brightlayer-ui/colors';
import { Box, Button, CircularProgress, Theme, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { FilledTextField } from '../../components/FilledTextField';
import { UserSelectors } from '../../features/userSlice';
import { useAlert } from '../../lib/AlertContext';
import { OrganizationService } from '../../services/organization/organization.service';
import { Organization } from '../../services/organization/types';
import { UserRole } from '../../services/user/types';
import { OrganizationFormValidations } from '../validations';

const styles = {
    sectionHeader: {
        paddingBottom: 2,
    },
    title: {
        color: Colors.blue['500'],
    },
    content: {
        color: Colors.black['500'],
    },
    textAndTitle: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '20px',
    },
    organizationNameForm: {
        display: 'flex',
        flexDirection: 'column',
    },
    textField: {
        maxWidth: '400px',
        color: Colors.white[200],
        width: '100%',
    },
    formHelperText: {
        marginLeft: '0px',
    },
    divider: {
        marginBottom: '25px',
    },
    buttonContainer: (theme: Theme): object => ({
        paddingLeft: theme.spacing(1),
    }),
    submitButton: {
        maxWidth: '90px',
        marginTop: '20px',
        textTransform: 'uppercase',
    },
    circularProgress: {
        color: 'white',
    },
    container: {
        paddingLeft: '25px',
        paddingTop: '25px',
    },
    helperTextStyle: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
};

type EditOrganizationFormProps = {
    organization: Organization;
};

export const EditOrganizationForm: React.FC<EditOrganizationFormProps> = (props) => {
    const { organization } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showErrorToUser } = useAlert();

    /*To get the organizations from the Redux store
          This is used to validate duplicate organization names*/
    const organizations = useAppSelector(UserSelectors.userOrganizationRoles);
    const organizationNameArray = organizations
        .filter((org) => org.role === UserRole.OWNER)
        .map((org) => org.organizationName);
    const [isOrganizationNameDuplicateError, setOrganizationNameDuplicateError] = useState(false);

    const [organizationName, setOrganizationName] = useState(organization.name);
    const [organizationDescription, setOrganizationDescription] = useState(organization.description);
    const [pointOfContactName, setPointOfContactName] = useState(organization.pointOfContact.name);
    const [pointOfContactEmail, setPointOfContactEmail] = useState(organization.pointOfContact.email);
    const [pointOfContactPhoneNumber, setPointOfContactPhoneNumber] = useState(organization.pointOfContact.phoneNumber);

    const [organizationNameError, setOrganizationNameError] = useState(false);
    const [organizationDescriptionError, setOrganizationDescriptionError] = useState(false);
    const [pointOfContactNameError, setPointOfContactNameError] = useState(false);
    const [pointOfContactEmailError, setPointOfContactEmailError] = useState(false);
    const [pointOfContactPhoneNumberError, setPointOfContactPhoneNumberError] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const renderFailedToUpdateModal = (): void => {
        showErrorToUser({
            errorTitle: t('EDIT_ORGANIZATION_PAGE.FAILED_TO_EDIT_TITLE'),
            errorDescription: t('EDIT_ORGANIZATION_PAGE.FAILED_TO_EDIT_MESSAGE'),
            confirmText: t('COMMON.TRY_AGAIN'),
            cancelText: t('COMMON.CANCEL'),
            onCancel: () => {
                navigate(`/organizations/${organization.id}`);
            },
        });
    };

    const onSubmit = async (): Promise<void> => {
        const org = {
            id: organization.id,
            name: organizationName,
            description: organizationDescription,
            pointOfContact: {
                name: pointOfContactName,
                email: pointOfContactEmail,
                phoneNumber: pointOfContactPhoneNumber,
            },
        };

        setIsLoading(true);
        try {
            await OrganizationService.updateOrganization(org);
            setIsLoading(false);
            navigate(`/organizations/${organization.id}`);
        } catch {
            renderFailedToUpdateModal();
        } finally {
            setIsLoading(false);
        }
    };

    const unchanged = useMemo(
        () =>
            organizationName.trim() === organization.name &&
            organizationDescription.trim() === organization.description &&
            pointOfContactName.trim() === organization.pointOfContact.name &&
            pointOfContactEmail.trim() === organization.pointOfContact.email &&
            pointOfContactPhoneNumber === organization.pointOfContact.phoneNumber,
        [organizationName, organizationDescription, pointOfContactName, pointOfContactEmail, pointOfContactPhoneNumber]
    );

    const missingRequiredFields = useMemo(
        () =>
            organizationName === '' ||
            organizationDescription === '' ||
            pointOfContactName === '' ||
            pointOfContactEmail === '',
        [organizationName, organizationDescription, pointOfContactName, pointOfContactEmail]
    );

    const invalidForm = useMemo(
        () =>
            organizationNameError ||
            isOrganizationNameDuplicateError ||
            organizationDescriptionError ||
            pointOfContactNameError ||
            pointOfContactEmailError ||
            pointOfContactPhoneNumberError,
        [
            organizationNameError,
            isOrganizationNameDuplicateError,
            organizationDescriptionError,
            pointOfContactNameError,
            pointOfContactEmailError,
            pointOfContactPhoneNumberError,
        ]
    );

    const validateOrgName = (userInput: string): void => {
        const orgNameValidatorResult = OrganizationFormValidations.organizationNameValidator(userInput);

        setOrganizationNameError(!orgNameValidatorResult);
        if (userInput !== organization.name) {
            const isDuplicateName = OrganizationFormValidations.organizationDuplicateNameValidator(
                userInput,
                organizationNameArray
            );
            setOrganizationNameDuplicateError(isDuplicateName);
        }
    };

    return (
        <Box sx={styles.container}>
            <Box sx={styles.organizationNameForm}>
                <Box sx={styles.sectionHeader}>
                    <Typography variant="h6">{t('EDIT_ORGANIZATION_PAGE.TITLE')}</Typography>
                </Box>
                <Box>
                    <FilledTextField
                        label={t('EDIT_ORGANIZATION_PAGE.ORG_NAME_LABEL')}
                        data-testid="organization-name-text-box"
                        sx={styles.textField}
                        error={organizationNameError || isOrganizationNameDuplicateError}
                        inputProps={{ maxLength: 256 }}
                        helperText={
                            <Box sx={styles.helperTextStyle}>
                                <span>
                                    {isOrganizationNameDuplicateError
                                        ? t('ERROR.DUPLICATE_ORG_NAME_ERROR')
                                        : organizationNameError
                                        ? t('CREATE_ORGANIZATION_PAGE.ORG_NAME_ERROR')
                                        : undefined}
                                </span>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    {`${organizationName?.length || 0}/256`}
                                </Typography>
                            </Box>
                        }
                        value={organizationName}
                        onChange={(event): void => {
                            setOrganizationName(event.target.value);
                            validateOrgName(event.target.value.trim());
                        }}
                        onBlur={(): void => {
                            if (organizationName) {
                                validateOrgName(organizationName.trim());
                            }
                        }}
                    />

                    <FilledTextField
                        label={t('EDIT_ORGANIZATION_PAGE.ORG_DESC_LABEL')}
                        data-testid="organization-description-text-box"
                        sx={styles.textField}
                        inputProps={{ maxLength: 1024 }}
                        error={organizationDescriptionError}
                        helperText={
                            <Box sx={styles.helperTextStyle}>
                                <span>
                                    {organizationDescriptionError
                                        ? t('EDIT_ORGANIZATION_PAGE.ORG_DESCRIPTION_ERROR')
                                        : undefined}
                                </span>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    {`${organizationDescription?.length || 0}/1024`}
                                </Typography>
                            </Box>
                        }
                        value={organizationDescription}
                        onChange={(event): void => {
                            setOrganizationDescriptionError(false);
                            setOrganizationDescription(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (organizationDescription !== undefined) {
                                setOrganizationDescriptionError(
                                    !OrganizationFormValidations.organizationDescriptionValidator(
                                        organizationDescription.trim()
                                    )
                                );
                            }
                        }}
                    />
                </Box>
                <Box sx={styles.sectionHeader}>
                    <Typography variant="h6">{t('EDIT_ORGANIZATION_PAGE.POC_DETAILS')}</Typography>
                </Box>
                <Box>
                    <FilledTextField
                        label={t('EDIT_ORGANIZATION_PAGE.POC_NAME_LABEL')}
                        data-testid="point-of-contact-name-text-box"
                        sx={styles.textField}
                        inputProps={{ maxLength: 256 }}
                        error={pointOfContactNameError}
                        helperText={
                            <Box sx={styles.helperTextStyle}>
                                <span>
                                    {pointOfContactNameError ? t('EDIT_ORGANIZATION_PAGE.POC_NAME_ERROR') : undefined}
                                </span>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                    {`${pointOfContactName?.length || 0}/256`}
                                </Typography>
                            </Box>
                        }
                        value={pointOfContactName}
                        onChange={(event): void => {
                            setPointOfContactNameError(false);
                            setPointOfContactName(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (pointOfContactName !== undefined) {
                                setPointOfContactNameError(
                                    !OrganizationFormValidations.pointOfContactNameValidator(pointOfContactName.trim())
                                );
                            }
                        }}
                    />
                    <FilledTextField
                        label={t('EDIT_ORGANIZATION_PAGE.POC_EMAIL_LABEL')}
                        data-testid="point-of-contact-email-text-box"
                        sx={styles.textField}
                        FormHelperTextProps={{ sx: styles.formHelperText }}
                        error={pointOfContactEmailError}
                        helperText={pointOfContactEmailError ? t('EDIT_ORGANIZATION_PAGE.POC_EMAIL_ERROR') : undefined}
                        value={pointOfContactEmail}
                        onChange={(event): void => {
                            setPointOfContactEmailError(false);
                            setPointOfContactEmail(event.target.value);
                        }}
                        onBlur={(): void => {
                            if (pointOfContactEmail !== undefined) {
                                setPointOfContactEmailError(
                                    !OrganizationFormValidations.pointOfContactEmailValidator(
                                        pointOfContactEmail.trim()
                                    )
                                );
                            }
                        }}
                    />
                    <FilledTextField
                        label={t('EDIT_ORGANIZATION_PAGE.POC_PHONE_NUMBER_LABEL')}
                        data-testid="point-of-contact-phone-number-text-box"
                        sx={styles.textField}
                        FormHelperTextProps={{ sx: styles.formHelperText }}
                        error={pointOfContactPhoneNumberError}
                        helperText={
                            pointOfContactPhoneNumberError
                                ? t('EDIT_ORGANIZATION_PAGE.POC_PHONE_NUMBER_ERROR')
                                : undefined
                        }
                        value={pointOfContactPhoneNumber}
                        onChange={(event): void => {
                            setPointOfContactPhoneNumberError(false);
                            setPointOfContactPhoneNumber(event.target.value === '' ? undefined : event.target.value);
                        }}
                        onBlur={(): void => {
                            if (pointOfContactPhoneNumber) {
                                setPointOfContactPhoneNumberError(
                                    !OrganizationFormValidations.pointOfContactPhoneNumberValidator(
                                        pointOfContactPhoneNumber.trim()
                                    )
                                );
                            }
                        }}
                    />
                </Box>
                <Button
                    sx={styles.submitButton}
                    id="submit-button"
                    variant="contained"
                    color="primary"
                    onClick={(): void => void onSubmit()}
                    disabled={unchanged || invalidForm || missingRequiredFields}
                >
                    {isLoading ? (
                        <Box data-testid="update-org-spinner">
                            <CircularProgress sx={styles.circularProgress} size={20} variant={'indeterminate'} />
                        </Box>
                    ) : (
                        t('COMMON.SUBMIT')
                    )}
                </Button>
            </Box>
        </Box>
    );
};
